import Image from 'next/image';
import { debounce } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { A11y, Mousewheel } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Box, Flex, Heading, useBreakpointValue } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { ContentContainer } from '~/components/container';
import { trackEvent } from '~/features/analytics/track-event';
import { popularCatalogItems } from '~/features/page-template/templates/home/model/recommendations';
import { useI18n } from '~/hooks/use-i18n';

import { useExperimentalSegmentEvents } from '../hooks';

import { CatalogCard } from './catalog-card';

import 'swiper/css';
import 'swiper/css/mousewheel';

export function UdacityTopProgramsSection() {
  const { t } = useTranslation();
  function handleBrowseProgramsClick() {
    trackEvent({
      type: 'click',
      name: 'Homepage CTA Clicked',
      label: 'browse_programs_clicked',
    });
  }

  return (
    <Flex
      as='section'
      bgGradient='linear(270deg, #001E65 0%, #0A0B0F 100%)'
      flexDirection='column'
      gap={{
        base: 8,
        md: '56px',
      }}
      padding={{
        base: '56px 0px',
        md: '112px 0px',
      }}
    >
      <ContentContainer
        alignItems='center'
        contentMaxWidth='content-max-width-md'
        display='flex'
        flexDirection='row'
        gap={6}
      >
        <Box
          display={{
            base: 'none',
            md: 'block',
          }}
        >
          <Image alt='' height={64} src='/images/experiments/ab_plp_redesign_2025/nanodegree-icon.svg' width={64} />
        </Box>
        <Flex flex={1} flexDirection='column' gap={1}>
          <Flex
            alignItems='center'
            as='p'
            color='blue.300'
            fontFamily='condensed'
            fontSize='0.9375rem'
            fontWeight='500'
            gap={4}
            letterSpacing={1.06}
            textTransform='uppercase'
            _after={{
              content: '""',
              display: 'block',
              width: '100%',
              height: '0.5px',
              backgroundColor: 'blue.300',
              flex: 1,
            }}
          >
            {t('home.topPrograms.pretitle')}
          </Flex>
          <Flex gap={4} justifyContent='space-between'>
            <Heading
              as='h2'
              color='white'
              fontWeight='600'
              lineHeight='1.1'
              fontSize={{
                base: '1.75rem',
                md: '2.25rem',
              }}
            >
              {t('home.topPrograms.title')}
            </Heading>
            <ButtonLink
              buttonProps={{
                display: {
                  base: 'none',
                  md: 'flex',
                },
                variant: 'solid',
                colorScheme: 'blue',
              }}
              linkProps={{
                href: '/catalog',
                onClick: handleBrowseProgramsClick,
              }}
            >
              {t('common.browsePrograms')}
            </ButtonLink>
          </Flex>
        </Flex>
      </ContentContainer>
      <Box overflow='hidden' position='relative'>
        <UdacityTopProgramsCarousel />
        <Box
          bgGradient='linear(270deg, rgba(10, 11, 15, 0) 0%, #0A0B0F 100%)'
          bottom={0}
          left={0}
          pointerEvents='none'
          position='absolute'
          top={0}
          width={{
            base: '52px',
            md: '112px',
          }}
        />
        <Box
          bgGradient='linear(270deg, #001E65 0%, rgba(0, 30, 101, 0) 100%)'
          bottom={0}
          pointerEvents='none'
          position='absolute'
          right={0}
          top={0}
          width={{
            base: '52px',
            md: '112px',
          }}
        />
      </Box>
      <ContentContainer
        display={{
          base: 'flex',
          md: 'none',
        }}
      >
        <ButtonLink
          buttonProps={{
            variant: 'solid',
            colorScheme: 'blue',
            width: 'full',
            onClick: handleBrowseProgramsClick,
          }}
          linkProps={{
            href: '/catalog',
          }}
        >
          {t('common.browsePrograms')}
        </ButtonLink>
      </ContentContainer>
    </Flex>
  );
}

function UdacityTopProgramsCarousel() {
  const { t, i18n } = useI18n();
  const isMobile = useBreakpointValue(
    {
      base: true,
      md: false,
    },
    { fallback: 'md' }
  );
  const isExperimentalTrackEventsEnabled = useExperimentalSegmentEvents('dino')?.variant === 'on';
  const handleSlideMove = debounce(() => {
    if (isExperimentalTrackEventsEnabled) {
      trackEvent({
        name: 'Homepage CTA Clicked',
        type: 'click',
        label: 'course_scroll_clicked',
      });
    }
  }, 100);

  return (
    <Box
      isolation='isolate'
      margin='0 auto'
      maxWidth='2880px'
      width='100%'
      sx={{
        '.swiper .swiper-slide': {
          height: 'auto',
          display: 'flex',
        },
      }}
    >
      <Swiper
        key={isMobile ? 'mobile' : 'desktop'}
        centeredSlides={isMobile}
        direction='horizontal'
        modules={[A11y, Mousewheel]}
        slidesPerView='auto'
        spaceBetween={24}
        mousewheel={{
          forceToAxis: true,
          thresholdDelta: 10,
        }}
        loop
        onBeforeInit={(swiper) => {
          swiper.changeLanguageDirection(i18n.dir());
        }}
        onSliderMove={() => {
          handleSlideMove();
        }}
      >
        {popularCatalogItems.map((catalogItem) => {
          const localizedCatalogItem = {
            ...catalogItem,
            title: t(`catalog.popularItems.${catalogItem.key}.title`),
            summary: t(`catalog.popularItems.${catalogItem.key}.summary`),
          };

          return (
            <SwiperSlide
              key={catalogItem.key}
              style={{
                width: isMobile ? '170px' : '237px',
              }}
            >
              <CatalogCard
                catalogItem={localizedCatalogItem}
                width='100%'
                onClick={() =>
                  trackEvent({
                    type: 'click',
                    name: 'Homepage CTA Clicked',
                    label: 'Top Rated Programs',
                    value: catalogItem.title ?? '',
                    action: 'homepage_ndcard_clicked',
                  })
                }
              />
            </SwiperSlide>
          );
        })}
      </Swiper>
    </Box>
  );
}
